import {
    SimplePageContentWrapper,
    FormPageLayout
} from '@flexinet/ui-components';
import { Card, Text, Button, Group, Badge, Stack, Grid } from '@mantine/core';
import { useProducts } from '../hooks/useProducts';
import { Product, ProductStatus } from '../types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';

const ProductSelectionCard = ({ product, isSelected, onSelect }: { 
    product: Product, 
    isSelected: boolean, 
    onSelect: (product: Product) => void 
}) => {
    return (
        <Card 
            shadow="sm" 
            padding="xl" 
            radius="md" 
            withBorder 
            h="100%" 
            style={isSelected ? { borderColor: '#228be6', borderWidth: 2 } : {}}
        >
            <Stack gap="md">
                <Group justify="space-between">
                    <Text fw={500} size="lg">{product.name}</Text>
                    <Badge color={product.status === ProductStatus.Active ? 'green' : 'gray'}>
                        {product.status}
                    </Badge>
                </Group>
                
                {product.shortDescription && (
                    <Text size="sm" c="dimmed">{product.shortDescription}</Text>
                )}
                
                {product.freeTrial && (
                    <Badge variant="light" color="blue">Free Trial Available</Badge>
                )}
                
                {product.prices?.items && product.prices.items.length > 0 && (
                    <Stack gap="xs">
                        <Text fw={500} size="sm">Pricing:</Text>
                        {product.prices.items.map((price) => (
                            <Card key={price.id} padding="sm" withBorder>
                                <Group justify="space-between" align="center">
                                    <Group>
                                        <Text fw={500}>
                                            ${price.amount}
                                        </Text>
                                        <Badge variant="light">
                                            {price.billingPeriod}
                                        </Badge>
                                    </Group>
                                </Group>
                            </Card>
                        ))}
                    </Stack>
                )}
                
                <Button 
                    fullWidth
                    variant={isSelected ? "filled" : "light"}
                    onClick={() => onSelect(product)}
                >
                    {isSelected ? "Selected" : "Select Product"}
                </Button>
            </Stack>
        </Card>
    );
};

const ProductSelectionHeader = ({ selectedProduct, onProceed }: { 
    selectedProduct: Product | null, 
    onProceed: () => void 
}) => (
    <Group justify="flex-end" p="md">
        {selectedProduct && (
            <Button onClick={onProceed} size="lg">
                Subscribe to {selectedProduct.name}
            </Button>
        )}
    </Group>
);

export const ProductSelectionPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const preSelectedProductId = searchParams.get('productId');
    const isOnboarding = searchParams.get('onboarding') === 'true';
    
    const { products, isLoading: productsLoading } = useProducts();
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    
    // Auto-select product if coming from URL
    useEffect(() => {
        if (preSelectedProductId && products) {
            const product = products.find(p => p.id === preSelectedProductId);
            if (product) setSelectedProduct(product);
        }
    }, [preSelectedProductId, products]);

    const handleProductSelect = (product: Product) => {
        setSelectedProduct(product);
    };

    const handleProceed = () => {
        if (!selectedProduct) return;
        
        // Navigate to subscription flow with selected product
        const returnTo = isOnboarding ? '/products?success=true' : '/subscriptions';
        navigate(`/products/subscribe?productId=${selectedProduct.id}&returnTo=${encodeURIComponent(returnTo)}`);
    };

    if (productsLoading) {
        return <Text>Loading...</Text>;
    }

    return (
        <FormPageLayout 
            title="Select a Product"
            pageNavigation={<ProductSelectionHeader selectedProduct={selectedProduct} onProceed={handleProceed} />}
        >
            <SimplePageContentWrapper>
                <Stack gap="md" mb="lg">
                    <Text c="dimmed">Choose the product you'd like to subscribe to</Text>
                </Stack>
                <Grid gutter="xl">
                    {products.filter(product => product.status === ProductStatus.Active).map((product) => (
                        <Grid.Col key={product.id} span={{ base: 12, sm: 6 }}>
                            <ProductSelectionCard 
                                product={product} 
                                isSelected={selectedProduct?.id === product.id}
                                onSelect={handleProductSelect}
                            />
                        </Grid.Col>
                    ))}
                </Grid>
                {products.filter(product => product.status === ProductStatus.Active).length === 0 && (
                    <Text ta="center" c="dimmed">No active products available</Text>
                )}
            </SimplePageContentWrapper>
        </FormPageLayout>
    );
};


